import React, { useCallback, useContext, useEffect, useState } from "react";
import { GenericPopUp } from "../../../components/popup/genericPopUp";
import { manageEntityApprovalRowsData, manageOrderRowsData, popupType } from "../../../types/types";
import BorderHeading from "../../../components/heading/BorderHeading";
import moment from "moment";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  DataGrid,
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { Button, Stack, TextField, Tooltip } from "@mui/material";
import { adminValidationConfig } from "./adminValidationConfig";
import { getOrderList } from "../../../services/manageOrderService";
import { useNavigate } from "react-router-dom";
import "../../../features/secondaryOrder/ManageSecondaryOrder.css"
import "../../../features/viewOrder/ViewOrder.css";
import "../../../features/manageBasket/ManageBasket.css";
import { Loader } from "../../../components/loader/Loader";
import { getEntityApprovals } from "../../../services/adminValidationService";
import { isGapMoreThanNDays } from "../../../utils/calenderUtils";

export const AdminValidationPage = () => {
  const [startDateForAttribute, setStartDateForAttribute] = useState<string>(
    moment().format("YYYY-MM-DD 00:00:00")
  );

  const [endDateForAttribute, setEndDateForAttribute] = useState<string>(
    moment().format("YYYY-MM-DD 23:59:59")
  );

  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState<string>("");
  const [data, setData] = useState<manageEntityApprovalRowsData[]>([]);
  const [rows, setRows] = useState<manageEntityApprovalRowsData[]>([]);
  const [startDate, setStartDate] = useState<Dayjs>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();

  const openPopup = useCallback((message: string, type: popupType) => {
    setPopupType(type);
    setGenericMessage(message);
    setOpenGenericModal(true);
  }, []);

  const isStartDateIsGreaterThanEndDate = (
    startDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs
  ) => {
    const diff = endDate.diff(startDate);
    return diff < 0;
  };

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
  const validDateRange = ConfigDictionary.dateRangeConfig ?? 31;
  function canWeMakeRequest() {
    if (startDate && endDate && isGapMoreThanNDays(startDate, endDate, validDateRange)) {
      openPopup(`You can't select date range more than ${validDateRange} days!`, "Alert");
      return false;
    }
    if (
      startDate &&
      endDate &&
      isStartDateIsGreaterThanEndDate(startDate, endDate)
    ) {
      openPopup("To Date should be greater than from date.", "Alert");
      return false;
    }
    return true;
  }

  const handleApply = async () => {
    if (canWeMakeRequest()) {
      const start = moment(startDate.toISOString()).format(
        "YYYY-MM-DD 00:00:00"
      );
      const end = moment(endDate.toISOString()).format("YYYY-MM-DD 23:59:59");
      setStartDateForAttribute(start);
      setEndDateForAttribute(end);
    }
  };

  const handleClearAll = () => {
    setStartDateForAttribute(moment().format("YYYY-MM-DD 00:00:00"));
    setEndDateForAttribute(moment().format("YYYY-MM-DD 23:59:59"));
    setStartDate(dayjs());
    setEndDate(dayjs());
  };

  const getAllOrderData = async () => {
    try {
      const response = await getEntityApprovals(startDateForAttribute,endDateForAttribute);
      if (response.data.features.length > 0) {
        let responseArray: manageEntityApprovalRowsData[] = [];
        response.data.features.forEach((item: any, index: any) => {
          const {referenceId,id,creationTime,status} = item;
          responseArray.push({referenceId,id,creationTime,status});
        });

        setRows(responseArray);
        setData(responseArray);
        setLoader(false);
      } else {
        setLoader(false);
        
      }
    } catch (error) {
      setRows([]);
      openPopup("Something went wrong", "Error");
      setLoader(false);
    }
  };

  useEffect(() => {
    setLoader(true)
    getAllOrderData();
  }, [startDateForAttribute]);

  const columns: GridColDef[] = adminValidationConfig.columns.map(
    (columnsMetaData) => {
      if (columnsMetaData.headerName === "Status") {
        return {
          disableColumnMenu: true,
          field: columnsMetaData.mappedValue,
          headerName: columnsMetaData.headerName,
          sortable: false,
          width: columnsMetaData.width,
          headerAlign: "left",
          align: "left",
          renderHeader: (params: GridColumnHeaderParams) => (
            <Tooltip title={columnsMetaData.headerName}>
              <b>{columnsMetaData.headerName.toUpperCase()}</b>
            </Tooltip>
          ),
          renderCell: (params: GridRenderCellParams) => {
            return (
              <p className={`status-div ${params.row.status.toLowerCase()}`}>
                {params.row.status}
              </p>
            );
          },
        };
      } else {
        return {
          disableColumnMenu: true,
          field: columnsMetaData.mappedValue,
          headerName: columnsMetaData.headerName,
          sortable: false,
          width: columnsMetaData.width,
          headerAlign: "left",
          align: "left",
          renderHeader: (params: GridColumnHeaderParams) => (
            <Tooltip title={columnsMetaData.headerName}>
              <b>{columnsMetaData.headerName.toUpperCase()}</b>
            </Tooltip>
          ),
        };
      }
    }
  );

  const addActionAttributes = (
    field: string,
    headerName: "",
    action: (params: GridRenderCellParams) => JSX.Element
  ) => {
    columns.push({
      field,
      headerName,
      sortable: false,
      minWidth: 90,
      flex: 0.5,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderCell: action,
    });
  };

  adminValidationConfig.Actions.forEach((actionName: string) => {
    switch (actionName) {
      case "update":
        addActionAttributes("Update", "", (params: GridRenderCellParams) => {
          return (
            <button
              className="manage-order-table-button"
              onClick={() => {
                navigate("/adminValidationPage/userDetails", {
                  state: {
                    row: params.row,
                  },
                });
              }}
            >
              Update
            </button>
          );
        });
        break;
    }
  });

  const filterData = (searchedValue: string) => {
    searchedValue = searchedValue.toLowerCase();
    const finalData = data.filter((eachRow: any) => {
      const allValues = Object.values(eachRow);
      return allValues.find((eachValue: any) => {
        return eachValue?.toString().toLowerCase().includes(searchedValue);
      });
    });
    setRows(finalData);
  };

  return (
    <div className="manage-order-parent">
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
      <BorderHeading>Admin Approval</BorderHeading>
      <div className="filter-searchbar">
        <div className="support-utility-dates">
          <Stack spacing={2.8} direction={"row"}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              className="dateEnter"
            >
              <DatePicker
                label="From Date"
                inputFormat="DD-MM-YYYY"
                value={startDate}
                maxDate={dayjs()}
                onChange={(value) => {
                  if (value) {
                    setStartDate(value);
                  }
                }}
                renderInput={(props) => (
                  <TextField {...props} disabled size="small" />
                )}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                inputFormat="DD-MM-YYYY"
                value={endDate}
                maxDate={dayjs()}
                onChange={(value) => {
                  if (value) {
                    setEndDate(value);
                  }
                }}
                renderInput={(props) => (
                  <TextField {...props} disabled size="small" />
                )}
              />
            </LocalizationProvider>
          </Stack>
          <div className="reset-and-apply-button-manage-order">
            <Button
              variant="contained"
              sx={{ backgroundColor: "#237C9D" }}
              onClick={handleApply}
            >
              Apply
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#237C9D" }}
              onClick={handleClearAll}
            >
              Reset
            </Button>
          </div>
        </div>
        <div className="manage-order-search-bar">
          <TextField
            id="outlined-basic"
            label="Search"
            variant="outlined"
            size="small"
            fullWidth
            autoComplete="off"
            onChange={(e) => {
              filterData(e.target.value);
            }}
          />
        </div>
      </div>
      {loader ? (
        <Loader />
      ) : (<div className="manage-order-table">
        <DataGrid
          disableRowSelectionOnClick={true}
          sx={{
            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
          }}
          rows={rows}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: "id", sort: "asc" }],
            },
          }}
        />
      </div>)}
    </div>
  );
};
